import React from 'react';
import PropTypes, { any } from 'prop-types';

import Img from '../../../components/Img';
import Description from '../../../components/text/Description';
import CopyToClipboard from '../../../components/text/CopyToClipboard';
import PlayerBookmark from '../../../components/SavedLists/PlayerBookmark';
import PlayerRatingV2 from '../PlayerRatingV2';
import { gameTypes } from '../../../utils/constants';
import AvatarV2 from '../../../components/cards/PlayerCard/ListViewComponents/AvatarV2';
import { sportTypeIds } from '../../../utils/eventConstants';
import { isPlayerCheckedIntoDraw } from '../../../utils/events';

import styles from '../drawEdit.scss';

const EMPTY_SAVED_LIST_IDS = [];

const AllPlayersTeamItemV3 = (props) => {
    const { selected, team, playersDictByMemberId, sportTypeId, showHighSchoolName, drawsDict, isFlexLeagueEvent, showSaveBookmark, 'data-testid': dataTestId } = props;

    const renderPlayer = (player, playerTestId) => {
        const description = [
            player.gender,
            showHighSchoolName && player.highSchool ? player.highSchool : player.location?.display,
        ];

        return (
            <div className={`row`}>
                <div className={`col-md-3 d-flex aic`}>
                    <AvatarV2
                        playerImages={player.playerProfileImages}
                        player={player}
                        className={styles.avatar}
                        data-testid={playerTestId && `${playerTestId}.avatar`}
                    />
                    <div className="player-details">
                        <div className="name text-dark" data-testid={playerTestId && `${playerTestId}.name`}>{player.firstName} {player.lastName}</div>
                        <div className="location text-normal text-gray">
                            <Description desc={description} data-testid={playerTestId && `${playerTestId}.description`} />
                        </div>
                    </div>
                </div>
                <PlayerRatingV2
                    {...props}
                    player={player}
                    sportTypeId={sportTypeId}
                    ratingType={gameTypes.SINGLES}
                    data-testid={playerTestId && `${playerTestId}.singles-rating`}
                />
                <PlayerRatingV2
                    {...props}
                    player={player}
                    sportTypeId={sportTypeId}
                    ratingType={gameTypes.DOUBLES}
                    data-testid={playerTestId && `${playerTestId}.doubles-rating`}
                />
                <div className="col-md-1 d-flex aic">
                    <div className="player-details">
                        <div className="name text-dark" data-testid={playerTestId && `${playerTestId}.age`}>
                            {player.age}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 d-flex align-items-center">
                    <div className="player-details">
                        <div className="name text-dark ellipsis" title={player.email}>
                            <CopyToClipboard className="ellipsis" text={player.email} data-testid={playerTestId && `${playerTestId}.email`} />
                        </div>
                        <div className="location text-dark ellipsis" title={player.phone}>
                            <CopyToClipboard className="ellipsis" text={player.phone} data-testid={playerTestId && `${playerTestId}.phone`} />
                        </div>
                        <span className={`${styles.sms}`}>
                            {player.smsOptIn && <span className={`${styles.on}`} data-testid={playerTestId && `${playerTestId}.sms-on`}>SMS ON</span>}
                            {!player.smsOptIn && <span className={`${styles.off}`} data-testid={playerTestId && `${playerTestId}.sms-off`}>SMS OFF</span>}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderHomeCourt = (player, homeCourtTestId) => {
        return (
            <div className="player-details overflow-hidden" title={player.homeCourt}>
                <div className="name text-dark ellipsis" data-testid={homeCourtTestId && `${homeCourtTestId}.city`}>
                    {player.homeCourt?.split(',')[0] || ''}
                </div>
                <div className="location text-normal text-gray" data-testid={homeCourtTestId && `${homeCourtTestId}.detail`}>
                    {player.homeCourt?.split(',').slice(1).join(',').trim() || ''}
                </div>
            </div>
        );
    };

    const getDrawName = (draw) => {
        const drawsDictObj = drawsDict[draw.id];
        if (drawsDictObj?.isChildDraw) {
            return `${drawsDict[drawsDictObj.parentDrawId]?.name}, ${drawsDictObj?.name}`;
        }
        return drawsDictObj?.name;
    };

    return (
        <div className={`item ${selected ? 'selected' : ''} ${styles.allPlayersItem}`} onClick={() => { props.toggleTeamSelect(team.id); }} data-testid={dataTestId}>
            <div className="checkbox-wrapper">
                <div className={`checkbox-check`}><Img src="/img/icons/checkmark-icon.svg" /></div>
            </div>
            <div className="bl mr12" />
            <div className="flex-grow">
                {!!team.members?.length && team.members.map((memberId, i) => {
                    const memberTestId = dataTestId && `${dataTestId}.member-${memberId}`;
                    const member = playersDictByMemberId[memberId];
                    return (
                        <div key={memberId} className={`row ${i !== team.members?.length - 1 ? 'bb pb8 mb8' : ''}`}>
                            <div className={`col-md-5`}>
                                {renderPlayer(playersDictByMemberId[memberId], memberTestId)}
                            </div>
                            <div className="col-md-1 draws-list d-flex aic" data-testid={memberTestId && `${memberTestId}.reg-status`}>
                                {team.regStatus || 'Main'}
                            </div>
                            {isFlexLeagueEvent && (
                                <div className={`col-md-1 d-flex aic`}>
                                    {renderHomeCourt(playersDictByMemberId[memberId], memberTestId && `${memberTestId}.home-court`)}
                                </div>
                            )}
                            <div className={`col-md-2 draws-list d-flex flex-column jcc`}>
                                {team.divisions?.length ? (
                                    <>
                                        {team.divisions.map(div => <div key={div.id} title={div.name} data-testid={memberTestId && `${memberTestId}.division-${div.id}`}>{div.name}</div>)}
                                    </>
                                ) : <span className="text-error text-medium" data-testid={memberTestId && `${memberTestId}.not-in-division`}>Not in division</span>}
                            </div>
                            <div className={`col-md-2 draws-list d-flex flex-column jcc`}>
                                {team.draws?.length ? (
                                    <>
                                        {team.draws.map(draw => (
                                            <div key={draw.id} title={getDrawName(draw)} data-testid={memberTestId && `${memberTestId}.draw-${draw.id}`}>
                                                {isPlayerCheckedIntoDraw(playersDictByMemberId[memberId], draw?.id) && <span className="utr-icon icon-check-mark-green mr4 checkin-mark" title="Player checked in" />}
                                                {getDrawName(draw)}
                                            </div>
                                        ))}
                                    </>
                                ) : <span className="text-error text-medium" data-testid={memberTestId && `${memberTestId}.not-in-draw`}>Not in draw</span>}
                            </div>
                            <div className="col-md-1 d-flex aic jcfe">
                                {showSaveBookmark && member?.playerId && (
                                    <PlayerBookmark
                                        playerId={member.playerId}
                                        playerName={[member.firstName, member.lastName].filter(Boolean).join(' ') || undefined}
                                        isSaved={member.isSaved}
                                        savedListIds={EMPTY_SAVED_LIST_IDS}
                                        className="text-teal"
                                        data-testid={memberTestId && `${memberTestId}.player-bookmark`}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

AllPlayersTeamItemV3.propTypes = {
    selected: PropTypes.bool,
    team: PropTypes.objectOf(any).isRequired,
    sportTypeId: PropTypes.number,
    showHighSchoolName: PropTypes.bool,
    toggleTeamSelect: PropTypes.func,
    playersDictByMemberId: PropTypes.arrayOf(any).isRequired,
    isFlexLeagueEvent: PropTypes.bool,
    showSaveBookmark: PropTypes.bool,
    'data-testid': PropTypes.string,
};

AllPlayersTeamItemV3.defaultProps = {
    selected: false,
    sportTypeId: sportTypeIds.TENNIS,
    showHighSchoolName: false,
    toggleTeamSelect: () => { },
    isFlexLeagueEvent: false,
    showSaveBookmark: false,
    'data-testid': undefined,
};

export default AllPlayersTeamItemV3;
